import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

function generateId() {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export default function ChatBox({ apiUrl, focusNodeId = null }) {
  const { t } = useTranslation();

  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem('ubxat_chat_history');
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.map((m) => ({ ...m, timestamp: new Date(m.timestamp) }));
      }
    } catch (_) {}
    return [{
      id: generateId(),
      text: t('content:chatWelcome'),
      sender: 'assistant',
      timestamp: new Date(),
    }];
  });
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [confidenceHelpOpenId, setConfidenceHelpOpenId] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem('ubxat_chat_history', JSON.stringify(messages));
    } catch (_) {}
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (bottomRef.current && typeof bottomRef.current.scrollIntoView === 'function') {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || isLoading) return;

    const userMsg = { id: generateId(), text, sender: 'user', timestamp: new Date() };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      const data = await apiUrl.chatWithKnowledge(text, {
        contextLimit: 80,
        includeSources: true,
        focusNodeId,
      });
      const assistantMsg = {
        id: generateId(),
        text: data.answer || data.response || '',
        sender: 'assistant',
        timestamp: new Date(),
        sources: data.sources || [],
        source_details: Array.isArray(data.source_details) ? data.source_details : [],
        confidence: typeof data.confidence === 'number' ? data.confidence : null,
        confidence_note: data.confidence_note ?? null,
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          id: generateId(),
          text: `${t('content:errorPrefix')}: ${err.message}`,
          sender: 'assistant',
          timestamp: new Date(),
          isError: true,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, apiUrl, focusNodeId, t]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const copyMessage = async (msg) => {
    await navigator.clipboard.writeText(msg.text);
    setCopiedId(msg.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatTime = (date) => {
    try {
      return new Intl.DateTimeFormat('ca', { hour: '2-digit', minute: '2-digit' }).format(date);
    } catch (_) {
      return '';
    }
  };

  const downloadConversation = useCallback(() => {
    const lines = messages.map((m) => {
      const role = m.sender === 'user' ? 'user' : 'assistant';
      const ts = m.timestamp instanceof Date ? m.timestamp.toISOString() : '';
      let block = `[${ts}] ${role}\n${m.text || ''}`;
      if (m.sources && m.sources.length) {
        block += `\n${t('content:sourceLabel')}: ${JSON.stringify(m.sources)}`;
      }
      if (m.source_details && m.source_details.length) {
        block += `\nsource_details: ${JSON.stringify(m.source_details)}`;
      }
      if (typeof m.confidence === 'number') {
        block += `\n${t('ui:confidence')}: ${Math.round(m.confidence * 100)}%`;
      }
      if (m.confidence_note) {
        block += `\nconfidence_note: ${m.confidence_note}`;
      }
      return block;
    });
    const blob = new Blob([lines.join('\n\n---\n\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ubxat-chat-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }, [messages, t]);

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full max-w-2xl mx-auto">
      <p id="chat-section-hint" className="text-label text-ink-muted mb-2 max-w-prose shrink-0">
        {t('content:chatSectionIntro')}
      </p>
      {/* Messages: flex-1 fills viewport; inner min-h-full + justify-end avoids a huge empty band above the input */}
      <div
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden py-4 flex flex-col overscroll-contain touch-pan-y"
        aria-live="polite"
        aria-label={t('content:chatTitle')}
      >
        <div className="min-h-full flex flex-col justify-end gap-6">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex shrink-0 ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {msg.sender === 'assistant' ? (
              <div className="max-w-prose min-w-0 w-full">
                <div
                  className={`pl-4 border-l-2 ${
                    msg.isError ? 'border-signal-error' : 'border-accent'
                  }`}
                >
                  <p
                    className={`text-sm leading-relaxed ${
                      msg.isError ? 'text-signal-error' : 'text-ink-secondary'
                    }`}
                  >
                    {msg.text}
                  </p>
                  {typeof msg.confidence === 'number' && (
                    <div className="mt-2">
                      <div className="flex flex-wrap items-center gap-2 text-label">
                        <button
                          type="button"
                          onClick={() =>
                            setConfidenceHelpOpenId((id) => (id === msg.id ? null : msg.id))
                          }
                          aria-expanded={confidenceHelpOpenId === msg.id}
                          aria-controls={`confidence-help-${msg.id}`}
                          className="text-ink-muted hover:text-ink-secondary transition-colors border-b border-dotted border-ink-muted"
                        >
                          {t('ui:confidence')}: {Math.round(msg.confidence * 100)}%
                          <span className="text-ink-muted ml-1" aria-hidden>ⓘ</span>
                          <span className="sr-only"> — {t('ui:confidenceTooltipToggle')}</span>
                        </button>
                        <span className="text-ink-muted">·</span>
                        <span className={
                          (msg.rows_used ?? 0) === 0
                            ? 'text-signal-error'
                            : 'text-ink-secondary'
                        }>
                          {t('ui:grounding')}: {t('ui:groundingRows', { n: msg.rows_used ?? 0 })}
                          {' · '}
                          {t('ui:groundingSources', {
                            n: (msg.source_details?.length ?? 0),
                          })}
                        </span>
                        {(msg.total_tokens_in != null || msg.total_tokens_out != null) && (
                          <>
                            <span className="text-ink-muted">·</span>
                            <span className="text-ink-muted" title={t('ui:tokensTooltip')}>
                              {msg.total_tokens_in ?? 0}→{msg.total_tokens_out ?? 0} tok
                            </span>
                          </>
                        )}
                        {msg.trace_id && (
                          <>
                            <span className="text-ink-muted">·</span>
                            <button
                              type="button"
                              onClick={() => {
                                try {
                                  window.dispatchEvent(new CustomEvent('neorag:openTrace', {
                                    detail: { traceId: msg.trace_id },
                                  }));
                                } catch (_) {}
                              }}
                              className="text-accent-text hover:underline"
                            >
                              {t('ui:viewReasoning')}
                            </button>
                          </>
                        )}
                      </div>

                      {(msg.rows_used ?? 0) === 0 && (
                        <p className="mt-1.5 text-xs text-signal-error/90 max-w-md leading-relaxed border-l-2 border-signal-error pl-2">
                          {t('ui:groundingNone')}
                        </p>
                      )}

                      {confidenceHelpOpenId === msg.id && (
                        <div
                          id={`confidence-help-${msg.id}`}
                          role="note"
                          className="text-xs text-ink-muted mt-1.5 max-w-md leading-relaxed border-l border-border-strong pl-2 space-y-2"
                        >
                          <p>{t('ui:confidenceTooltip')}</p>
                          {msg.confidence_note === 'no_graph_match' && (
                            <p className="text-accent-text/90">
                              {t('ui:confidenceNoteNoMatch')}
                            </p>
                          )}
                          {msg.confidence_note === 'retrieval' && (
                            <p>{t('ui:confidenceNoteRetrieval')}</p>
                          )}
                          {msg.cypher_summary && (
                            <details>
                              <summary className="cursor-pointer text-ink-muted">
                                {t('ui:cypherUsed')}
                              </summary>
                              <pre className="mt-1 whitespace-pre-wrap break-words text-[11px] bg-surface-raised/60 p-2 rounded">
                                {msg.cypher_summary}
                              </pre>
                            </details>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {Boolean(
                    (msg.source_details && msg.source_details.length > 0) ||
                      (msg.sources &&
                        msg.sources.filter((s) => s !== 'Base de conocimiento').length > 0)
                  ) && (
                    <div
                      className="mt-2 max-h-[min(50vh,20rem)] overflow-y-auto overscroll-contain pr-1 rounded-md border border-border-subtle/60 bg-surface/50 custom-scrollbar"
                      aria-label={t('content:sourceChipsLabel')}
                    >
                  {msg.source_details && msg.source_details.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 p-2 pb-1">
                      {msg.source_details.map((d, i) => {
                        const label = (d.title && String(d.title).trim()) || d.ref || `(${i})`;
                        return (
                          <span
                            key={`src-chip-${msg.id}-${i}`}
                            className="text-label max-w-[14rem] truncate px-2 py-0.5 rounded bg-surface-hover/90 border border-border-strong text-ink-secondary"
                            title={d.ref != null ? String(d.ref) : undefined}
                          >
                            {label}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {msg.sources && msg.sources.filter(s => s !== 'Base de conocimiento').length > 0 && (
                    <div className="flex flex-col gap-2 px-2 pb-2 pt-0">
                      {msg.sources.filter(s => s !== 'Base de conocimiento').map((src, i) => {
                        const srcStr = String(src);
                        const det = (msg.source_details || []).find((d) => {
                          if (!d) return false;
                          const r = d.ref != null ? String(d.ref) : '';
                          if (r && (r === srcStr || srcStr === r)) return true;
                          if (r && (srcStr.includes(r) || r.includes(srcStr))) return true;
                          const title = d.title != null ? String(d.title) : '';
                          if (title && (srcStr.includes(title) || title.includes(srcStr))) return true;
                          return false;
                        });
                        const label = det && det.title ? `${det.title}` : srcStr;
                        const sub = det && det.ref && String(det.ref) !== label ? ` · ${det.ref}` : '';
                        return (
                          <details
                            key={`${srcStr}-${i}`}
                            className="text-label bg-surface-hover/80 rounded border border-border-strong px-2 py-1"
                          >
                            <summary className="cursor-pointer text-ink-secondary hover:text-ink">
                              <span className="font-medium">{label}</span>
                              {sub && <span className="text-ink-muted font-normal">{sub}</span>}
                              {det && typeof det.relationship_count === 'number' && det.relationship_count > 0 && (
                                <span className="text-ink-muted ml-1">
                                  · {det.relationship_count} {t('content:relationsShort')}
                                </span>
                              )}
                            </summary>
                            {det && det.type_label && (
                              <p className="text-ink-secondary mt-2 text-xs">{det.type_label}</p>
                            )}
                            {det && det.preview && String(det.preview).trim() !== '' && (
                              <pre className="mt-2 text-xs text-ink-secondary whitespace-pre-wrap font-sans leading-relaxed border-t border-border-strong/80 pt-2 max-h-48 overflow-y-auto custom-scrollbar">
                                {det.preview}
                              </pre>
                            )}
                            {det && det.related && det.related.length > 0 && (
                              <ul className="mt-2 space-y-0.5 text-xs text-ink-muted list-disc list-inside">
                                {det.related.slice(0, 8).map((r, j) => (
                                  <li key={j}>
                                    {r.direction === 'out' ? '→' : '←'} {r.relationship_type}: {r.label || r.node_id}
                                  </li>
                                ))}
                              </ul>
                            )}
                            {det && !det.preview && (!det.related || det.related.length === 0) && !det.type_label && (
                              <p className="text-ink-muted mt-2 text-xs italic">{t('content:sourceNoExtraMeta')}</p>
                            )}
                          </details>
                        );
                      })}
                    </div>
                  )}
                    </div>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-3 pl-4">
                  <span className="text-label text-ink-muted">{formatTime(msg.timestamp)}</span>
                  <button
                    onClick={() => copyMessage(msg)}
                    aria-label={t('ui:copy')}
                    className="text-label text-ink-muted hover:text-ink-secondary transition-colors"
                  >
                    {copiedId === msg.id ? t('ui:copied') : t('ui:copy')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="max-w-prose text-right">
                <p className="text-sm text-ink-secondary leading-relaxed">{msg.text}</p>
                <span className="text-label text-ink-muted mt-1 block">
                  {formatTime(msg.timestamp)}
                </span>
              </div>
            )}
          </div>
        ))}

        {isLoading && (
          <div role="status" className="pl-4 border-l-2 border-accent shrink-0">
            <p className="text-sm text-ink-muted animate-pulse">{t('ui:loading')}</p>
          </div>
        )}

        <div ref={bottomRef} className="h-px shrink-0" aria-hidden="true" />
        </div>
      </div>

      {/* Input area */}
      <div className="flex-shrink-0 border-t border-border-subtle pt-4 pb-2">
        <div className="flex items-end gap-4">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            placeholder={`${t('content:chatTitle')}...`}
            disabled={isLoading}
            className="flex-1 bg-surface-raised border border-border-subtle rounded p-3 text-sm text-ink-secondary placeholder-ink-muted resize-none focus:outline-none focus:border-border-strong transition-colors disabled:opacity-50"
          />
          <button
            onClick={sendMessage}
            disabled={isLoading || !input.trim()}
            aria-label={t('ui:send')}
            className="text-sm text-accent-text hover:text-accent-hover disabled:text-ink-muted transition-colors font-medium pb-3 flex-shrink-0"
          >
            {t('ui:send')}
          </button>
        </div>
        <div className="flex items-center justify-between mt-1 gap-4 flex-wrap">
          <span className="text-label text-ink-muted">{t('ui:shiftEnterHint')}</span>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={downloadConversation}
              className="text-label text-ink-muted hover:text-accent-text transition-colors"
            >
              {t('ui:downloadChat')}
            </button>
            <button
              type="button"
              onClick={() => {
                try { localStorage.removeItem('ubxat_chat_history'); } catch (_) {}
                setMessages([{
                  id: generateId(),
                  text: t('content:chatWelcome'),
                  sender: 'assistant',
                  timestamp: new Date(),
                }]);
              }}
              aria-label={t('ui:resetChat')}
              className="text-label text-ink-muted hover:text-ink-secondary transition-colors"
            >
              {t('ui:resetChat')}
            </button>
          </div>
        </div>
        <div className="mt-2 text-label text-ink-muted italic">
          {t('traceability:aiAct.disclosure')}
        </div>
      </div>
    </div>
  );
}
